import { commonConstants, serviceRequestConstants } from "../_redux/_constants";

/**
 * Get service request status label configuration
 * @param {Function} t - Translation function from useTranslation hook
 * @param {string} styleVariant - 'card' or 'detail' to determine className prefix
 * @returns {Object} Label map configuration
 */
export const getServiceRequestStatusConfig = (t, styleVariant = "card") => {
  const isDetailView = styleVariant === "detail";

  return {
    [serviceRequestConstants.FILTER_INITIATED_STATUS]: {
      text: t("INITIATED"),
      className: isDetailView
        ? "df-serv-req-status-side-orange"
        : "df-lbl-canceled",
    },
    [serviceRequestConstants.FILTER_ACCEPTED_STATUS]: {
      text: t("ACCEPTED"),
      className: isDetailView ? "df-serv-req-status-side" : "df-lbl-success",
    },
    [serviceRequestConstants.FILTER_SUBMITTED_STATUS]: {
      text: t("SUBMITTED"),
      className: isDetailView
        ? "df-serv-req-status-side-blue"
        : "df-lbl-success-blue",
    },
    [serviceRequestConstants.FILTER_SUCCESSFULLY_SUBMITTED_STATUS]: {
      text: t("SUCCESS"),
      className: isDetailView ? "df-serv-req-status-side" : "df-lbl-success",
    },
    [serviceRequestConstants.FILTER_COMPLETED_STATUS]: {
      text: t("COMPLETED"),
      className: isDetailView ? "df-serv-req-status-side" : "df-lbl-success",
    },
    [serviceRequestConstants.FILTER_DISPATCHED_STATUS]: {
      text: t("DISPATCHED"),
      className: isDetailView ? "df-serv-req-status-side" : "df-lbl-success",
    },
    [serviceRequestConstants.FILTER_SUBMISSION_FAILED_STATUS]: {
      text: t("FAILED"),
      className: isDetailView ? "df-serv-req-status-side-red" : "df-lbl-failed",
    },
    [serviceRequestConstants.FILTER_FAILED_STATUS]: {
      text: t("FAILED"),
      className: isDetailView ? "df-serv-req-status-side-red" : "df-lbl-failed",
    },
    [serviceRequestConstants.FILTER_DECLINED_STATUS]: {
      text: t("DECLINED"),
      className: isDetailView
        ? "df-serv-req-status-side-orange"
        : "df-lbl-declined",
    },
    [serviceRequestConstants.FILTER_CANCELED_STATUS]: {
      text: t("CANCELED"),
      className: isDetailView
        ? "df-serv-req-status-side-orange"
        : "df-lbl-canceled",
    },
  };
};

/**
 * Get service request status item for card view
 * @param {string} status - Status constant value
 * @param {Function} t - Translation function
 * @returns {Object|null} Status item with text and className
 */
export const getServiceRequestStatusLabel = (status, t) => {
  const labelMap = getServiceRequestStatusConfig(t, "card");
  return labelMap[status] || null;
};

/**
 * Get service request status item for detail view
 * @param {string} status - Status constant value
 * @param {Function} t - Translation function
 * @returns {Object|null} Status item with text and className
 */
export const getServiceRequestStatusDetail = (status, t) => {
  const labelMap = getServiceRequestStatusConfig(t, "detail");
  return labelMap[status] || null;
};

export function isServiceRequestSensitiveField(fieldMeta) {
  return fieldMeta?.isSensitive === commonConstants.YES_OPTION_TYPE;
}

export function maskServiceRequestSensitiveValue(value) {
  if (value == null || value === "") {
    return value;
  }

  return "*".repeat(String(value).length);
}

export function formatServiceRequestSensitiveDisplayValue(
  value,
  fieldMeta,
  fallback = "-",
) {
  if (value == null || value === "") {
    return fallback;
  }

  const displayValue = String(value);

  if (isServiceRequestSensitiveField(fieldMeta)) {
    return maskServiceRequestSensitiveValue(displayValue);
  }

  return displayValue;
}

export function getServiceRequestSensitiveFieldMeta(
  fieldCode,
  item,
  templateFieldsList = [],
) {
  if (item?.isSensitive != null) {
    return item;
  }

  return (
    templateFieldsList?.find((field) => field?.fieldCode === fieldCode) || item
  );
}

export function formatFieldValueDisplay(fieldValue, fallback = "-") {
  if (fieldValue == null || fieldValue === "") {
    return fallback;
  }

  if (Array.isArray(fieldValue)) {
    const parts = fieldValue
      .map((entry) => {
        if (entry == null) {
          return "";
        }

        if (typeof entry === "object") {
          return (
            entry.heading ??
            entry.dropdownValue ??
            entry.fieldValue ??
            entry.optionValue ??
            entry.id ??
            ""
          );
        }

        return String(entry);
      })
      .filter(Boolean);

    return parts.length ? parts.join(", ") : fallback;
  }

  if (typeof fieldValue === "object") {
    return (
      fieldValue.heading ??
      fieldValue.dropdownValue ??
      fieldValue.fieldValue ??
      fallback
    );
  }

  return String(fieldValue);
}

/**
 * Merge gateway service-request `data.response` wrapper with envelope fields.
 * Thin/empty inner objects must not discard outer responseCode/status fields.
 */
export function normalizeServiceRequestGatewayData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }

  const { response, ...envelope } = data;

  if (response && typeof response === "object" && !Array.isArray(response)) {
    return { ...envelope, ...response };
  }

  return { ...data };
}

/**
 * Normalize ajax payload `{ data, status, ... }` before Redux complete actions.
 */
export function normalizeServiceRequestGatewayPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { data: {} };
  }

  return {
    ...payload,
    data: normalizeServiceRequestGatewayData(payload.data),
  };
}
